import InventoryItemEventType from './enums/InventoryItemEventType';
import InventoryItemEventTypeTitle from './enums/InventoryItemEventTypeTitle';

/**
 * Деловая история оборудования: как читать событие жизненного цикла.
 *
 * Здесь всё, что можно проверить без браузера, — и именно поэтому оно здесь, а не
 * в компоненте: так же устроены inventoryCard.js, inventoryPreview.js и inventoryFiles.js.
 * Компонент отвечает за разметку и запрос, этот модуль — за решения: какие переходы
 * показывать, как называть пустую сторону, каким маркером метить событие.
 *
 * НИЧЕГО НЕ РЕЗОЛВИТСЯ ПО ССЫЛКАМ. Имена держателей, названия городов и статусов
 * приезжают готовыми вместе с событием — бэкенд снимает их в момент записи. Ходить
 * за справочником, чтобы подписать строку истории, нельзя не только из-за лишних
 * запросов: справочник с тех пор изменился, и подпись получилась бы сегодняшней,
 * а событие было вчерашним.
 */

/**
 * Как назвать пустую сторону перехода держателя.
 *
 * Именно словами, а не прочерком: «Не назначен» — это утверждение о состоянии вещи
 * («лежит на складе, ни за кем не числится»), а прочерк читается как «данных нет».
 */
export const NO_HOLDER = 'Не назначен';

/**
 * Прочерк для стороны, о которой сведений действительно нет.
 */
export const EMPTY_SIDE = '—';

/**
 * Типы событий, о которых этот фронт знает. Незнакомый тип не прячется — он приезжает
 * со своим названием в label и показывается нейтральным маркером.
 */
const KNOWN_TYPES = [
    InventoryItemEventType.RECEIVED,
    InventoryItemEventType.ISSUED,
    InventoryItemEventType.RETURNED,
    InventoryItemEventType.HOLDER_CHANGED,
    InventoryItemEventType.MOVED,
    InventoryItemEventType.MARKED_BROKEN,
    InventoryItemEventType.FAULT_CLEARED,
    InventoryItemEventType.REPAIR_SENT,
    InventoryItemEventType.REPAIR_RETURNED,
    InventoryItemEventType.WRITTEN_OFF,
];

/**
 * Название события.
 *
 * СНАЧАЛА ТО, ЧТО ПРИСЛАЛ СЕРВЕР. Свой словарь — только запасной: новый тип события
 * появится в ответе раньше, чем этот фронт будет выкачен, и жёсткая опора на локальный
 * словарь показала бы на его месте пустую строку. Последний рубеж — сам код: он заметен,
 * а пустое место нет.
 *
 * @param {Object} event
 * @returns {string}
 */
export function eventTitle(event) {
    const type = String(event?.type ?? '');

    return event?.label || InventoryItemEventTypeTitle[type] || type;
}

/**
 * Модификатор маркера события.
 *
 * Маркер, а не иконка на каждый тип: восемь разных значков в ленте из трёх строк
 * читаются хуже, чем восемь оттенков одной формы, — глаз ловит цвет раньше, чем рисунок.
 * Незнакомый тип получает нейтральный маркер, а не пропадает из ленты.
 *
 * @param {Object} event
 * @returns {string} Код типа или 'unknown'.
 */
export function markerModifier(event) {
    const type = String(event?.type ?? '');

    return KNOWN_TYPES.indexOf(type) === -1 ? 'unknown' : type;
}

/**
 * Одна сторона перехода держателя в виде строки.
 *
 * @param {Object|null} side
 * @returns {string}
 */
function holderSideText(side) {
    if (!side) {
        return NO_HOLDER;
    }

    return side.name || NO_HOLDER;
}

/**
 * Одна сторона перехода города в виде строки.
 *
 * @param {Object|null} side
 * @returns {string}
 */
function citySideText(side) {
    if (!side) {
        return EMPTY_SIDE;
    }

    return side.name || EMPTY_SIDE;
}

/**
 * Одна сторона перехода статуса в виде строки.
 *
 * @param {Object|null} side
 * @returns {string}
 */
function statusSideText(side) {
    if (!side) {
        return EMPTY_SIDE;
    }

    return side.title || EMPTY_SIDE;
}

/**
 * Переходы события — то, что в нём изменилось, строками «было → стало».
 *
 * ПУСТОЙ КЛЮЧ ЗНАЧИТ «НЕ МЕНЯЛОСЬ», и это решение бэкенда: у выдачи нет города,
 * у перемещения нет держателя, и присылать три пустых объекта на каждое событие значило
 * бы заставлять интерфейс отличать «не менялось» от «стало пустым» самостоятельно.
 * Здесь это различие только читается.
 *
 * Подпись у каждого перехода своя: у события может измениться и держатель, и город,
 * и статус сразу, и три безымянные строки «А → Б» подряд не читаются вовсе.
 *
 * @param {Object} event Событие из GET /inventory/items/{id}/lifecycle.
 * @returns {Array<{key: string, label: string, from: string, to: string}>}
 */
export function transitionsOf(event) {
    const transitions = [];

    if (event?.holder) {
        transitions.push({
            key: 'holder',
            label: 'Ответственный',
            from: holderSideText(event.holder.from),
            to: holderSideText(event.holder.to),
        });
    }

    if (event?.city) {
        transitions.push({
            key: 'city',
            label: 'Город',
            from: citySideText(event.city.from),
            to: citySideText(event.city.to),
        });
    }

    if (event?.status) {
        transitions.push({
            key: 'status',
            label: 'Статус',
            from: statusSideText(event.status.from),
            to: statusSideText(event.status.to),
        });
    }

    return transitions;
}

/**
 * Имя автора события.
 *
 * Из ответа, где оно снято в момент события: учётка могла быть удалена или сменить
 * фамилию, а история обязана говорить, кто выдал технику ТОГДА.
 *
 * @param {Object} event
 * @returns {string}
 */
export function actorName(event) {
    return event?.actor?.display_name || 'Автор неизвестен';
}
